import type { SVGProps } from 'react';

export type SemanticIconName =
  | 'brand-mark'
  | 'inbox'
  | 'writing'
  | 'discover'
  | 'draft'
  | 'archive'
  | 'search'
  | 'bell'
  | 'error'
  | 'success'
  | 'info'
  | 'offline'
  | 'arrow-right'
  | 'refresh'
  | 'sparkle';

type IconProps = SVGProps<SVGSVGElement> & { title?: string };

const paths: Record<SemanticIconName, (props: IconProps) => React.ReactNode> = {
  'brand-mark': () => (
    <>
      <path d="M4.25 6.5h15.5v11H4.25z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="m4.7 7.15 7.3 5.55 7.3-5.55" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 4.5c3.2-2.05 5.8-2.7 8.2-2.35" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity=".7" />
      <circle cx="20.4" cy="3.6" r="1.35" fill="currentColor" opacity=".9" />
    </>
  ),
  inbox: () => (
    <>
      <path d="M4 5.5h16v13H4z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M4.5 12.5h4l1.3 2h4.4l1.3-2h4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  writing: () => (
    <>
      <path d="m5 19 1.2-4.4L16.8 4a1.9 1.9 0 0 1 2.7 2.7L8.9 17.3 5 19Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="m14.8 6 3.2 3.2M5 19l3.8-1.7" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </>
  ),
  discover: () => (
    <>
      <circle cx="12" cy="12" r="7.8" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="m14.8 9.2-1.6 4.1-4 1.5 1.5-4 4.1-1.6Z" fill="currentColor" />
    </>
  ),
  draft: () => (
    <>
      <path d="M6 4.5h9l3 3v12H6z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M14.5 4.8v3h3M9 11h6M9 14.5h4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  archive: () => (
    <>
      <path d="M4.5 7h15v12h-15zM3.5 4h17v3h-17z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M9 11.5h6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </>
  ),
  search: () => (
    <>
      <circle cx="10.8" cy="10.8" r="5.8" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="m15.2 15.2 4.3 4.3" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </>
  ),
  bell: () => (
    <>
      <path d="M6.3 16.8h11.4l-1.1-1.7v-3.3a4.6 4.6 0 0 0-9.2 0v3.3l-1.1 1.7Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M10.2 19.2h3.6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </>
  ),
  error: () => (
    <>
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 8v4.5M12 16.1v.1" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  success: () => (
    <>
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="m8.2 12 2.5 2.5 5.3-5.1" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  info: () => (
    <>
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 10.8v5M12 8.1v.1" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  offline: () => (
    <>
      <path d="M4.5 9.7a11.2 11.2 0 0 1 15 0M7.4 12.7a7 7 0 0 1 9.2 0M10.3 15.7a2.7 2.7 0 0 1 3.4 0" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="m5 5 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </>
  ),
  'arrow-right': () => <path d="M4.5 12h14m-5.5-5 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />,
  refresh: () => <path d="M18.5 8.5V5m0 0H15m3.5 0a7.6 7.6 0 1 0 1.1 8.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />,
  sparkle: () => <path d="m12 3 1.4 5.6L19 10l-5.6 1.4L12 17l-1.4-5.6L5 10l5.6-1.4L12 3Zm6.2 12.8.6 2.4 2.2.6-2.2.6-.6 2.4-.6-2.4-2.2-.6 2.2-.6.6-2.4Z" fill="currentColor" />,
};

export interface SemanticIconProps extends Omit<IconProps, 'title'> {
  name: SemanticIconName;
  size?: number | string;
  label?: string;
}

export function SemanticIcon({ name, size = 24, label, className, ...props }: SemanticIconProps) {
  const labelProps = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true };
  return (
    <svg
      {...props}
      {...labelProps}
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      focusable="false"
    >
      {paths[name]({})}
    </svg>
  );
}
